package com.arenaproplay.admin.ui.components.charts.svg

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

/**
 * Night Arena donut chart — stroke-dasharray technique on a base ring, butt
 * caps with 2% gap per slice, -90° start. Ported from kontaq QpDonut
 * (PlatformCharts.kt).
 *
 * @param slices      data slices (name + value + colorHex)
 * @param sizePx      outer size in pixels
 * @param strokeWidth ring width in SVG user units (default matches mockup)
 * @param modifier    standard Kobweb modifier
 */
@Composable
fun Donut(
    slices: List<DonutSlice>,
    sizePx: Int = 72,
    strokeWidth: Int = 12,
    modifier: Modifier = Modifier,
) {
    val svg = buildDonutSvg(slices, strokeWidth)
    Div(
        attrs = modifier
            .width(sizePx.px)
            .height(sizePx.px)
            .styleModifier { property("flex-shrink", "0") }
            .toAttrs {
                ref { el ->
                    el.innerHTML = svg
                    onDispose { el.innerHTML = "" }
                }
            }
    )
}

// ── SVG string builder ─────────────────────────────────────────────────────

private fun buildDonutSvg(slices: List<DonutSlice>, strokeWidth: Int): String {
    // viewBox is always 100×100; r is sized to leave strokeWidth/2 gap at edges
    val r = (50.0 - strokeWidth / 2.0 - 1.0).coerceAtLeast(8.0)
    val circumference = 2.0 * kotlin.math.PI * r

    // M4: compute total from clamped values so a negative+positive pair can't
    //     let the positive slice exceed circumference
    val clampedValues = slices.map { it.value.coerceAtLeast(0.0) }
    val total = clampedValues.sum().coerceAtLeast(0.0001)

    // H1: use stroke-linecap="butt" (no bleed-over between adjacent slices);
    //     add a 2% gap per slice for visual breathing room
    val gapFrac = 0.02
    val gapLen = gapFrac * circumference

    val emptyRing = """<svg viewBox="0 0 100 100" width="100%" xmlns="http://www.w3.org/2000/svg">
<circle cx="50" cy="50" r="${fmt(r)}" fill="none" stroke="rgba(255,255,255,.06)" stroke-width="$strokeWidth"/>
</svg>"""

    if (slices.isEmpty()) return emptyRing

    return buildString {
        append("<svg viewBox=\"0 0 100 100\" width=\"100%\" xmlns=\"http://www.w3.org/2000/svg\">")
        // Base ring — subtle fill to show where the donut lives on dark bg
        append("<circle cx=\"50\" cy=\"50\" r=\"${fmt(r)}\" fill=\"none\"")
        append(" stroke=\"rgba(255,255,255,.06)\" stroke-width=\"$strokeWidth\"/>")

        // Each slice is a circle with stroke-dasharray; accumulate offset
        var acc = 0.0
        slices.forEachIndexed { idx, slice ->
            val v = clampedValues[idx]
            val frac = v / total
            // Shrink dash by gap to leave breathing room; keep gap at end of dashLen
            val dashLen = (frac * circumference - gapLen).coerceAtLeast(0.0)
            val dashGap = circumference - dashLen
            append("<circle cx=\"50\" cy=\"50\" r=\"${fmt(r)}\" fill=\"none\"")
            append(" stroke=\"${slice.colorHex}\" stroke-width=\"$strokeWidth\"")
            append(" stroke-dasharray=\"${fmt(dashLen)} ${fmt(dashGap)}\"")
            append(" stroke-dashoffset=\"${fmt(-acc)}\"")
            append(" stroke-linecap=\"butt\"")
            append(" transform=\"rotate(-90 50 50)\"/>")
            acc += frac * circumference  // advance by full slice fraction (incl. gap)
        }

        append("</svg>")
    }
}
